import React, { useState } from 'react'
import { gql, useMutation, useQuery } from '@apollo/client'
import { Box, Collapse, TextField, Typography } from '@mui/material'
import { FEE_QUERY_FIELDS, FeeCompareTable, changedFees, showFee } from './FeeFields'
import { Icon, T } from './onboardingDesign'
import { Badge, FieldLabel, LinkButton, Notice, OutlineButton, PrimaryButton } from './onboardingUi'

/**
 * Super admin: agents' proposed fees waiting for a decision (backend
 * utils/merchantInvites.js). Approving applies them and emails the merchant
 * the link; rejecting keeps the application with RushMyFood and tells the
 * agent. Shows nothing when there is nothing to decide.
 */

export const FEE_REQUESTS = gql`
  query MerchantFeeRequests {
    merchantFeeRequests {
      _id partnerSignupId restaurantName contactName contactEmail agentName createdAt linkSent sendRequested
      fees { ${FEE_QUERY_FIELDS} }
      standard { ${FEE_QUERY_FIELDS} }
    }
  }
`
export const REVIEW = gql`
  mutation ReviewMerchantFeeRequest($id: ID!, $approve: Boolean!, $note: String) {
    reviewMerchantFeeRequest(id: $id, approve: $approve, note: $note) { _id status }
  }
`

function Request({ r, onDecided }) {
  const [review, { loading }] = useMutation(REVIEW)
  const [open, setOpen] = useState(false)
  const [rejecting, setRejecting] = useState(false)
  const [note, setNote] = useState('')
  const [error, setError] = useState(null)
  const changed = changedFees(r.fees, r.standard)
  // The agent already sent it for signature: approving emails the merchant.
  const sends = r.sendRequested && !r.linkSent

  const decide = async (approve) => {
    setError(null)
    try {
      await review({ variables: { id: r._id, approve, note: note.trim() || null } })
      onDecided(approve
        ? sends
          ? `Approved. ${r.contactEmail} was emailed the link to ${r.restaurantName}'s application, and ${r.agentName || 'the agent'} was told.`
          : `Approved. ${r.agentName || 'The agent'} was told; ${r.restaurantName}'s application goes to ${r.contactEmail} when they send it for signature.`
        : `Not approved. ${r.agentName || 'The agent'} was told; the application stays unsent until they change the fees or send it at the standard ones.`)
    } catch (e) {
      setError(e.message)
    }
  }

  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.5, borderTop: `1px solid ${T.divider}` }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 240, flex: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 16, color: T.text }}>{r.restaurantName}</Typography>
          <Typography sx={{ color: T.text2, fontSize: 14 }}>
            {r.contactName} · {r.contactEmail} · proposed by {r.agentName || 'an agent'} {r.createdAt ? `on ${new Date(r.createdAt).toLocaleDateString()}` : ''}
          </Typography>
          <Typography sx={{ color: sends ? T.amberText : T.hint, fontSize: 13, mt: 0.25 }}>
            {sends ? 'Sent for signature and waiting on these fees: approving emails the restaurant.' : 'Still being filled in by the agent.'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1.25 }}>
            {changed.map(([k, label, kind]) => (
              <Badge key={k} kind="amber">{`${label}: ${showFee(kind, r.fees[k])} (standard ${showFee(kind, r.standard[k])})`}</Badge>
            ))}
          </Box>
          <LinkButton onClick={() => setOpen(o => !o)} sx={{ mt: 0.5, ml: -1 }}>
            {open ? 'Hide all fees' : 'Show all fees'}
          </LinkButton>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <PrimaryButton tone="green" disabled={loading} busy={loading && !rejecting} onClick={() => decide(true)}>
            <Icon.Check size={16} />
            {sends ? 'Approve and send' : 'Approve fees'}
          </PrimaryButton>
          <OutlineButton disabled={loading} onClick={() => setRejecting(x => !x)}>
            Reject
          </OutlineButton>
        </Box>
      </Box>
      <Collapse in={open}><Box sx={{ mt: 1.5 }}><FeeCompareTable fees={r.fees} standard={r.standard} label="Proposed" /></Box></Collapse>
      <Collapse in={rejecting}>
        <Box sx={{ mt: 2 }}>
          <FieldLabel htmlFor={`reject-${r._id}`} label="Why (sent to the agent)" optional />
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <TextField id={`reject-${r._id}`} size="small" fullWidth value={note} onChange={e => setNote(e.target.value)} sx={{ flex: 1, minWidth: 240 }} inputProps={{ maxLength: 500 }} />
            <PrimaryButton disabled={loading} onClick={() => decide(false)}>
              Reject fees
            </PrimaryButton>
          </Box>
        </Box>
      </Collapse>
      {error && <Notice severity="error" sx={{ mt: 1.5, mb: 0 }}>{error}</Notice>}
    </Box>
  )
}

export default function FeeApprovals({ onChanged }) {
  const { data, refetch } = useQuery(FEE_REQUESTS, { fetchPolicy: 'network-only' })
  const [notice, setNotice] = useState(null)
  const requests = (data && data.merchantFeeRequests) || []
  if (!requests.length && !notice) return null

  const decided = async (message) => {
    setNotice(message)
    await refetch()
    if (onChanged) onChanged()
  }

  return (
    <Box sx={{ background: T.card, border: `1px solid ${T.amberBorder}`, borderRadius: '20px', overflow: 'hidden', mb: 3 }}>
      <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.25, background: T.amberTint, display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
        <Box sx={{ color: T.amberText, display: 'flex', mt: '2px' }}><Icon.Alert size={20} /></Box>
        <Box>
          <Typography component="h2" sx={{ fontWeight: 700, fontSize: 17, color: T.amberText }}>Fee approvals waiting ({requests.length})</Typography>
          <Typography sx={{ color: T.amberText, fontSize: 14 }}>
            Agents proposed these fees. The restaurant is emailed the application link only once you approve.
          </Typography>
        </Box>
      </Box>
      {notice && <Notice severity="success" sx={{ m: 2 }} onClose={() => setNotice(null)}>{notice}</Notice>}
      {requests.map(r => <Request key={r._id} r={r} onDecided={decided} />)}
    </Box>
  )
}
